'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import HealthcareHero from './HealthcareHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import IndustryCaseStudy from '@/components/industry/IndustryCaseStudy';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from HealthcareHero
   (Software Advice 2024, NMJI India Study, Healthgrades 2024). */
const METRICS = [
  { value: '70%', label: 'of patients check online reviews before choosing a hospital (Software Advice 2024)' },
  { value: '74%', label: 'of Indian adults influenced by social media in healthcare decision-making (NMJI India Study)' },
  { value: '1 in 3', label: 'patients share their hospital experience online — positive or negative (Healthgrades 2024)' },
];

export default function HealthcareBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('healthcare');

  const CHALLENGES = [{
    title: t("industries_healthcare.the_grievance_volume_problem"),
    desc: t("industries_healthcare.a_multi_location_hospital_chain_rec")
  }, {
    title: t("industries_healthcare.the_misinformation_flash_point"),
    desc: t("industries_healthcare.a_viral_post_claiming_a_hospitals_p")
  }, {
    title: t("industries_healthcare.the_review_blind_spot"),
    desc: t("industries_healthcare.each_facility_builds_its_own_review")
  }, {
    title: t("industries_healthcare.the_pharma_safety_gap"),
    desc: t("industries_healthcare.pharma_brand_monitoring_platform_in")
  }];

  const FAQS = [{
    q: t("industries_healthcare.what_is_a_healthcare_patient_experi"),
    a: t("industries_healthcare.one_workspace_for_patient_grievance")
  }, {
    q: t("industries_healthcare.how_does_locobuzz_help_multi_locati"),
    a: t("industries_healthcare.each_facility_gets_its_own_dashboar")
  }, {
    q: t("industries_healthcare.can_locobuzz_manage_patient_reviews"),
    a: t("industries_healthcare.yes_reviews_from_google_business_pr")
  }, {
    q: t("industries_healthcare.how_does_locobuzz_handle_medical_mi"),
    a: t("industries_healthcare.custom_keyword_alert_sets_fire_inst")
  }, {
    q: t("industries_healthcare.does_locobuzz_support_nabh_patient"),
    a: t("industries_healthcare.yes_complaint_resolution_timelines")
  }];

  const BG_TITLE = t('industries_healthcare.why_is_hospital_reputation_manageme');
  const BG_ACCENT = 'Clinical Priority';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">{t("industries_healthcare.locobuzz_is_a_global_ainative_custo")}</span>
      <span className="sr-only">{t("industries_healthcare.with_74_of_indian_adults_saying_soc")}</span>
      <span className="sr-only">{t("industries_healthcare.locobuzz_maps_six_purposebuilt_capa")}</span>
      <span className="sr-only">{t("industries_healthcare.healthcare_brands_using_locobuzz_re")}</span>

      <HealthcareHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_healthcare.why_hospital_reputation_management")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_healthcare.for_multilocation_chains_like_apoll")}
        imageSrc="/industries/why/healthcare.webp"
        imageAlt={t("industries_healthcare.healthcare_patient_experience_and_c")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_healthcare.what_challenges_does_a_patient_grie")}
        items={CHALLENGES}
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <IndustryCaseStudy {...C.caseStudy} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="healthcare-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
